import {
  type InstalledPlugin,
  isListedInAllApps,
  type PluginCatalogItem,
  type PluginCatalogSource,
  type PluginContributionKind,
  type PluginKind,
} from '@crowdex/plugin-contracts';
import { Switch } from '@crowdex/ui/components/switch';
import { cn } from '@crowdex/ui/lib/utils';
import { Check, LoaderCircle, RefreshCw } from 'lucide-react';
import { settingButtonClass } from '@/components/settings-page/setting-controls';
import { AppOrigin } from '@/components/sidebar/all-apps';
import {
  installedPluginKind,
  isPluginConnectionConfigurable,
} from '@/lib/plugins-page/plugin-status';
import { PluginLogo } from './plugin-logo';

export const CONTRIBUTION_LABELS: Record<PluginContributionKind, string> = {
  connectors: 'Services',
  skills: 'Skills',
  mcp: 'MCP',
  agents: 'Agents',
  resources: 'Resources',
  themes: 'Themes',
  apps: 'Apps',
};

export const SOURCE_LABELS: Record<PluginCatalogSource, string> = {
  codex: 'Codex marketplace',
  'claude-code': 'Claude Code marketplace',
  provisioned: 'Crowdex',
  account: 'Your account',
  local: 'Local',
  user: 'Your files',
  'connector-services': 'Crowdex catalog',
  'mcp-registry': 'MCP Registry',
  'acp-registry': 'ACP Registry',
};

const KIND_LABELS: Partial<Record<PluginKind, string>> = {
  app: 'App',
  editor: 'Editor',
  service: 'Service',
  provider: 'Provider',
  agent: 'Agent',
  skill: 'Skill',
};

/** What a plugin mainly is, for the line under its name (`Service`, `Agent`, `App`…). */
export function pluginKindLabel(plugin: PluginCatalogItem | InstalledPlugin): string {
  const derived = KIND_LABELS[installedPluginKind(plugin)];
  if (derived !== undefined) return derived;
  if ('crowdex' in plugin && plugin.crowdex?.page !== undefined) return 'App';
  if (plugin.service !== null || plugin.contributes.includes('connectors')) return 'Service';
  if (plugin.contributes.includes('agents')) return 'Agent';
  if (plugin.contributes.includes('apps')) return 'App';
  if (plugin.contributes.includes('skills')) return 'Skill';
  if (plugin.contributes.includes('mcp')) return 'MCP server';
  if (plugin.contributes.includes('themes')) return 'Theme';
  return 'Plugin';
}

/** The catalog button (D29): Get, Installed, or Connect when an account is still missing. */
export type CatalogAction = 'get' | 'installed' | 'connect' | 'soon';

export function catalogAction(
  plugin: PluginCatalogItem,
  local: InstalledPlugin | undefined,
  connected: boolean,
): CatalogAction {
  if (local === undefined) return plugin.availability === 'coming-soon' ? 'soon' : 'get';
  if (local.enabled && isPluginConnectionConfigurable(local) && !connected) return 'connect';
  return 'installed';
}

function CatalogButton({
  action,
  busy,
  name,
  onInstall,
  onConnect,
}: {
  action: CatalogAction;
  busy: boolean;
  name: string;
  onInstall: () => void;
  onConnect: () => void;
}) {
  if (action === 'installed')
    return (
      <span className="flex shrink-0 items-center gap-1 text-success text-sm">
        <Check aria-hidden className="size-3.5" /> Installed
      </span>
    );
  if (action === 'connect')
    return (
      <button
        aria-label={`Connect ${name}`}
        className={settingButtonClass()}
        onClick={onConnect}
        type="button"
      >
        Connect
      </button>
    );
  return (
    <button
      aria-label={action === 'soon' ? `${name} is coming soon` : `Get ${name}`}
      // An outline pill: a catalog has many, and a screen's one filled button is never a card's.
      className={settingButtonClass()}
      disabled={busy || action === 'soon'}
      onClick={onInstall}
      type="button"
    >
      {busy && <LoaderCircle className="size-3.5 animate-spin" />}
      {action === 'soon' ? 'Soon' : 'Get'}
    </button>
  );
}

/** A plugin of the catalog (D29, phone 26): what it is, one line, and its button. No switch. */
export function PluginCard({
  plugin,
  local,
  busy,
  connected,
  phone = false,
  featured = false,
  onInstall,
  onOpen,
}: {
  plugin: PluginCatalogItem;
  local: InstalledPlugin | undefined;
  busy: boolean;
  /** Whether the hub has an account of its services. */
  connected: boolean;
  phone?: boolean;
  featured?: boolean;
  onInstall: (plugin: PluginCatalogItem) => void;
  /** Opens the plugin's page. */
  onOpen: (plugin: PluginCatalogItem | InstalledPlugin) => void;
}) {
  const action = catalogAction(plugin, local, connected);
  const kind = pluginKindLabel(local ?? plugin);
  const button = (
    <CatalogButton
      action={action}
      busy={busy}
      name={plugin.displayName}
      onConnect={() => onOpen(local ?? plugin)}
      onInstall={() => onInstall(plugin)}
    />
  );
  if (phone)
    return (
      <article className="flex items-center gap-3 border-hairline border-b py-3 last:border-b-0">
        <button
          aria-label={`Open ${plugin.displayName}`}
          data-plugin-open={plugin.name}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          onClick={() => onOpen(local ?? plugin)}
          type="button"
        >
          <PluginLogo plugin={plugin} />
          <span className="min-w-0">
            <span className="block truncate font-semibold text-base">{plugin.displayName}</span>
            <span className="block truncate text-muted-foreground text-sm">
              {kind === 'Service' ? plugin.description : `${kind} · ${plugin.description}`}
            </span>
          </span>
        </button>
        {action === 'installed' ? (
          <span className="shrink-0 rounded-control bg-control px-3 py-1.5 text-sm text-success">
            Installed
          </span>
        ) : (
          button
        )}
      </article>
    );
  return (
    <article
      className={cn(
        'flex flex-col rounded-2xl border border-hairline p-3.5 transition-colors hover:border-foreground/20',
        featured && 'border-0 bg-card px-4 py-4',
      )}
    >
      <div className="flex items-center gap-3">
        <button
          aria-label={`Open ${plugin.displayName}`}
          data-plugin-open={plugin.name}
          className="group flex min-w-0 flex-1 items-center gap-3 text-left"
          onClick={() => onOpen(local ?? plugin)}
          type="button"
        >
          <PluginLogo plugin={plugin} size={featured ? 'large' : 'default'} />
          <span className="min-w-0">
            {featured && <span className="block text-muted-foreground text-xs">Featured</span>}
            <span
              className={cn(
                'block truncate font-semibold group-hover:underline',
                featured ? 'text-base' : 'text-sm',
              )}
            >
              {plugin.displayName}
            </span>
            <span className="block truncate text-muted-foreground text-xs">
              {featured ? plugin.description : kind}
            </span>
          </span>
        </button>
        {button}
      </div>
      {!featured && (
        <p className="mt-3 line-clamp-2 text-foreground/90 text-sm leading-5">
          {plugin.description}
        </p>
      )}
    </article>
  );
}

/** An installed plugin (D31, phone 28): its kind and state, its update, and the switch. */
export function InstalledPluginRow({
  plugin,
  busy,
  accounts,
  detail,
  onOpen,
  onToggle,
  onUpdate,
  onRefresh,
  phone = false,
}: {
  plugin: InstalledPlugin;
  busy: boolean;
  /** How many hub accounts its services have. */
  accounts: number;
  /** A line of its own instead of the kind (a custom plugin's source). */
  detail?: string;
  onOpen: (plugin: InstalledPlugin) => void;
  onToggle: (plugin: InstalledPlugin) => void;
  /** Installs its waiting update; absent without one. */
  onUpdate?: (() => void) | undefined;
  /** Builds it again from its folder on this computer; absent for other plugins. */
  onRefresh?: (() => void) | undefined;
  phone?: boolean;
}) {
  const pending = plugin.crowdex?.pending;
  const line =
    detail ??
    [
      pluginKindLabel(plugin),
      !plugin.enabled ? 'disabled' : null,
      plugin.enabled &&
      isListedInAllApps({ kind: plugin.crowdex?.kind, page: plugin.crowdex?.page })
        ? 'in All apps'
        : null,
      accounts > 0 ? `${accounts} ${accounts === 1 ? 'account' : 'accounts'}` : null,
      phone && pending ? `update ${plugin.version ?? ''} → ${pending.version}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  return (
    <div
      className={cn(
        'flex items-center gap-3 border-hairline border-b px-3.5 py-2.5 last:border-b-0',
        phone && 'px-4 py-3',
      )}
    >
      <button
        aria-label={`Open ${plugin.displayName}`}
        data-plugin-open={plugin.name}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
        onClick={() => onOpen(plugin)}
        type="button"
      >
        {/* Disabled dims its logo and greys its name; its lines keep their 4.5:1 (L2). */}
        <span className={cn('shrink-0', !plugin.enabled && 'opacity-50')} data-plugin-logo>
          <PluginLogo plugin={plugin} size="small" />
        </span>
        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                'truncate',
                phone ? 'text-base' : 'text-sm',
                !plugin.enabled && 'text-muted-foreground',
              )}
            >
              {plugin.displayName}
            </span>
            <AppOrigin service={plugin.crowdex?.service} />
          </span>
          <span className="block truncate text-muted-foreground text-xs">{line}</span>
        </span>
      </button>
      {busy && <LoaderCircle className="size-3.5 animate-spin text-muted-foreground" />}
      {onRefresh && (
        <button
          aria-label={`Refresh ${plugin.displayName} from its folder`}
          className="flex size-8 items-center justify-center rounded-control text-muted-foreground hover:bg-foreground/6 hover:text-foreground disabled:opacity-50"
          disabled={busy}
          onClick={onRefresh}
          title="Refresh from its folder"
          type="button"
        >
          <RefreshCw className="size-3.5" />
        </button>
      )}
      {!phone && onUpdate && pending && (
        <button className={settingButtonClass()} disabled={busy} onClick={onUpdate} type="button">
          {plugin.version ? `Update ${plugin.version} → ${pending.version}` : 'Update'}
        </button>
      )}
      <Switch
        aria-label={`${plugin.enabled ? 'Disable' : 'Enable'} ${plugin.displayName}`}
        checked={plugin.enabled}
        disabled={busy}
        onCheckedChange={() => onToggle(plugin)}
      />
    </div>
  );
}
